import {
  IconButton,
  makeStyles,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { InstagramLabelList_instagramLabels_edges_node } from "@saleor/inslabel/types/InstagramLabelList";
import { InslabelListUrlSortField } from "@saleor/inslabel/urls";
import { maybe, renderCollection, stopPropagation } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

export interface InslabelListProps
  extends ListProps,
    SortPage<InslabelListUrlSortField> {
  insLabels: InstagramLabelList_instagramLabels_edges_node[];
  onDelete: (id: string) => void;
}

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colActions: {
        width: 180
      },
      colMembers: {
        width: 180
      },
      colName: {
        width: "auto"
      }
    },
    colActions: {
      paddingRight: theme.spacing(),
      textAlign: "right"
    },
    colActionsHeader: {
      textAlign: "right"
    },
    colMembers: {
      textAlign: "left",
      paddingLeft: 40
    },
    colName: {
      paddingLeft: 0
    },
    link: {
      cursor: "pointer"
    }
  }),
  { name: "insLabelList" }
);
const numberOfColumns = 4;

const InslabelList: React.FC<InslabelListProps> = props => {
  const {
    insLabels,
    disabled,
    onNextPage,
    onPreviousPage,
    onRowClick,
    pageInfo,
    sort,
    onSort,
    onDelete,
    settings,
    onUpdateListSettings
  } = props;

  const classes = useStyles(props);

  return (
    <ResponsiveTable>
      <TableHead>
        <TableRow>
          <TableCellHeader
            direction={
              sort?.sort === InslabelListUrlSortField.name
                ? getArrowDirection(sort.asc)
                : undefined
            }
            arrowPosition="right"
            onClick={() => onSort(InslabelListUrlSortField.name)}
          >
            <FormattedMessage defaultMessage="Name" />
          </TableCellHeader>

          <TableCellHeader>
            <FormattedMessage defaultMessage="Actual Display Quantity" />
          </TableCellHeader>

          <TableCellHeader>
            <FormattedMessage defaultMessage="Correlation Number" />
          </TableCellHeader>

          <TableCellHeader></TableCellHeader>
        </TableRow>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          insLabels,
          insLabel => (
            <TableRow
              className={!!insLabel ? classes.link : undefined}
              hover={!!insLabel}
              key={insLabel ? insLabel.id : "skeleton"}
              onClick={insLabel ? onRowClick(insLabel.id) : undefined}
              data-test="id"
              data-test-id={maybe(() => insLabel.id)}
            >
              <TableCell className={classes.colName}>
                {insLabel ? (
                  <span data-test="name">{insLabel.name}</span>
                ) : (
                  <Skeleton />
                )}
              </TableCell>

              <TableCell className={classes.colMembers}>
                {insLabel ? (
                  insLabel.instagramList ? (
                    <span data-test="name">
                      {insLabel.instagramSortList.length +
                        insLabel.instagramUnsortList.length}
                    </span>
                  ) : (
                    <span data-test="name">0</span>
                  )
                ) : (
                  <Skeleton />
                )}
              </TableCell>

              <TableCell className={classes.colMembers}>
                {insLabel ? (
                  insLabel.instagramList ? (
                    <span data-test="name">
                      {insLabel.instagramList.length}
                    </span>
                  ) : (
                    <span data-test="name">0</span>
                  )
                ) : (
                  <Skeleton />
                )}
              </TableCell>

              <TableCell className={classes.colActions}>
                {insLabel ? (
                  <>
                    {insLabel.id && (
                      <IconButton
                        color="primary"
                        onClick={stopPropagation(() => onDelete(insLabel.id))}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No permission groups found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};
export default InslabelList;
